'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import Navbar from '../../../../../components/Navbar';
import Footer from '../../../../../components/Footer';
import InterestedPopup from '../../../../../components/InterestedPopup';

// Mock Civil Details
const mockCivilDetails = {
    'civil-001': {
        id: 'civil-001',
        title: 'Professional Home Painting Service',
        providerName: 'Color World Decors',
        price: '₹12 - ₹25',
        priceUnit: 'per sqft',
        rating: 4.8,
        reviews: 124,
        experience: '8 Years',
        jobsCompleted: '500+',
        description: 'We provide high-quality interior and exterior painting services. Our team consists of verified professionals who ensure dust-free experience and timely completion.',
        services: ['Interior Painting', 'Exterior Painting', 'Texture Design', 'Waterproofing', 'Wood Polishing'],
        locality: 'Anna Nagar',
        city: 'Chennai',
        images: [
            'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=800',
            'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800',
        ],
        portfolio: [
            { title: 'Modern Living Room', img: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=400' },
            { title: 'Texture Wall', img: 'https://images.unsplash.com/photo-1560184897-ae75f418493e?w=400' }
        ]
    }
};
const property = {
    agent: {
        name: "John Doe",
        role: "Property Agent"
    },
    costs: {
        rent: 25000,
        deposit: 300000
    }
};

export default function CivilDetailPage() {
    const params = useParams();
    const service = mockCivilDetails[params.id] || mockCivilDetails['civil-001'];
    const [showInterestedPopup, setShowInterestedPopup] = useState(false);
    const [isLoggedInContext, setIsLoggedInContext] = useState(false);

    return (
        <>
            <Navbar />
            <main className="min-h-screen pb-12">
                <div className="bg-white py-4">
                    <div className="container-custom">
                        <nav className="flex items-center gap-2 text-sm text-gray-500">
                            <Link href="/" className="hover:text-primary">Home</Link> <span>/</span>
                            <Link href="/civil-services" className="hover:text-primary">Civil Services</Link> <span>/</span>
                            <span className="text-gray-900 font-medium">{service.title}</span>
                        </nav>
                    </div>
                </div>

                <div className="container-custom">
                    <div className="grid lg:grid-cols-3 py-6 gap-8 items-start">
                        {/* Profile Header */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mb-6">
                                <div className="flex items-start gap-6">
                                    <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center text-3xl font-bold text-blue-600">
                                        {service.providerName.charAt(0)}
                                    </div>
                                    <div className="flex-1">
                                        <h1 className="text-2xl font-bold text-gray-900 mb-1">{service.title}</h1>
                                        <p className="text-gray-500 font-medium mb-3">by {service.providerName} • {service.locality}, {service.city}</p>

                                        <div className="flex items-center gap-6 text-sm">
                                            <div className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded">
                                                <span>★</span> <strong>{service.rating}</strong> ({service.reviews} Reviews)
                                            </div>
                                            <div className="flex items-center gap-1 text-gray-600">
                                                <strong>{service.experience}</strong> Exp
                                            </div>
                                            <div className="flex items-center gap-1 text-gray-600">
                                                <strong>{service.jobsCompleted}</strong> Jobs Done
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-8 pt-6 border-t border-gray-100">
                                    <h3 className="font-bold text-gray-900 mb-2">About the Service</h3>
                                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                                </div>
                            </div>

                            {/* Services List */}
                            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mb-6">
                                <h3 className="font-bold text-xl text-gray-900 mb-6">Services Offered</h3>
                                <div className="flex flex-wrap gap-3">
                                    {service.services.map(s => (
                                        <div key={s} className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium">
                                            {s}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Portfolio */}
                            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                                <h3 className="font-bold text-xl text-gray-900 mb-6">Recent Work Portfolio</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {service.portfolio.map((item, idx) => (
                                        <div key={idx} className="group relative rounded-lg overflow-hidden h-40">
                                            <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-sm font-bold">
                                                {item.title}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Booking Sidebar */}
                        <aside className="lg:col-span-1 space-y-6 lg:sticky lg:top-24">
                            {/* Rental Summary Card */}
                            <div className="bg-white rounded-2xl shadow-lg border border-primary/20 p-6 relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-purple-600"></div>

                                <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase">Listed by</h4>
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-10 h-10 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center text-white text-lg font-bold shadow-md">
                                        {property.agent.name.charAt(0)}
                                    </div>
                                    <div>
                                        <h5 className="font-bold text-gray-900">{property.agent.name}</h5>
                                        <p className="text-xs text-gray-500">{property.agent.role}</p>
                                    </div>
                                </div>
                                <div className="flex justify-between items-end mb-6">
                                    <div>
                                        <p className="text-gray-500 text-sm font-medium uppercase">Monthly Rent</p>
                                        <h3 className="text-2xl font-bold text-gray-900">₹{property.costs.rent != null ? property.costs.rent.toLocaleString() : 'N/A'}</h3>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-sm text-gray-500 block">Deposit</span>
                                        <span className="font-semibold text-gray-800">{property.costs.deposit != null ? `₹${(property.costs.deposit / 100000).toFixed(1)} Lac` : 'N/A'}</span>
                                    </div>
                                </div>

                                <button onClick={() => { setIsLoggedInContext(false); setShowInterestedPopup(true); }} className="w-full btn btn-primary py-3.5 text-base font-bold shadow-primary/30 shadow-md mb-4">
                                    I'm Interested
                                </button>

                                <div className="pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-400">
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                    Your details are safe with us
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </main>
            <InterestedPopup
                isOpen={showInterestedPopup}
                onClose={() => setShowInterestedPopup(false)}
                loggedInPerson={isLoggedInContext}
            />
            <Footer />
        </>
    );
}
